import {
  RefObject,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { reduce, filter, isArray, includes, isEqual, has } from 'lodash';
import moment from 'moment';

import { Nullable } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  BillingItemCompanyBase,
  BillingItemsFilter,
} from '~services/billingItem/types';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';
import {
  useSubscriptionFeatures,
  useParseLocationQuery,
  useFiltersValues,
} from '~hooks/index';
import { FiltersValuesReturn } from '~hooks/useFiltersValues';
import { getFeatureAvailableToDate } from '~utils/feature';
import { getItemValue } from '~utils/helpers';
import { formatterDate } from '~utils/formatter';
import { getCompanyToBillingItemId } from '~utils/billingItem';

import { CustomerPrediction } from './useCustomersAutocomplete';

export interface OrderBillingPendingListQueryParamsFilters {
  company?: string;
  integrationStores?: string | string[];
  giverCompanies?: string | string[];
  dateFrom?: Date;
  dateTo?: Date;
  storeName?: string;
}

export interface OrderBillingPendingListFilters {
  company: Nullable<BillingItemCompanyBase>;
  integrationStores: Nullable<BillingItemCompanyBase[]>;
  giverCompanies: Nullable<BillingItemCompanyBase[]>;
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;
  storeName: Nullable<string>;
  customers: Nullable<CustomerPrediction[]>;
}

type OrderBillingPendingListFiltersAppliedType = 'queryParams' | 'filters';

type OrderBillingPendingListFiltersApplied = Record<
  keyof OrderBillingPendingListFilters,
  OrderBillingPendingListFiltersAppliedType
>;

export type OrderBillingPendingListAutocompleteMultipleRef<T> =
  CustomAutocompleteRef<T, true>;

export interface OrderBillingPendingListParamsSelectData<T> {
  autocompleteRef: RefObject<OrderBillingPendingListAutocompleteMultipleRef<T>>;
  loading: boolean;
  list: T[];
}

export interface OrderBillingPendingListParamsSelects {
  companies: OrderBillingPendingListParamsSelectData<BillingItemCompanyBase>;
}

interface OrderBillingPendingListParams {
  initialFilters: OrderBillingPendingListFilters;
  selectsData: OrderBillingPendingListParamsSelects;
}

export interface OrderBillingPendingListReturn
  extends FiltersValuesReturn<OrderBillingPendingListFilters> {
  parseFiltersRequest: BillingItemsFilter;
}

export const useOrderBillingPendingListFilters = ({
  initialFilters,
  selectsData,
}: OrderBillingPendingListParams): OrderBillingPendingListReturn => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccesMgmBillingGiver = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmBillingGiver,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmBillingGiver),
      ),
    [hasAccessToFeature],
  );

  const queryFilters =
    useParseLocationQuery<OrderBillingPendingListQueryParamsFilters>({
      parseNumbers: false,
    });

  const [filtersApplied, setFilterApplied] =
    useState<OrderBillingPendingListFiltersApplied>(() => {
      const builder = reduce(
        initialFilters,
        (acc, _, key) => {
          const keyTyped = key as keyof OrderBillingPendingListFilters;

          acc[keyTyped] = has(queryFilters, keyTyped)
            ? 'queryParams'
            : 'filters';

          return acc;
        },
        {} as OrderBillingPendingListFiltersApplied,
      );

      return builder;
    });

  const { filters, setFilterByKey, clearFilters } =
    useFiltersValues(initialFilters);

  const getFilterAppliedByKey = useCallback(
    <Key extends keyof OrderBillingPendingListFiltersApplied>(
      key: Key,
    ): OrderBillingPendingListFiltersApplied[Key] =>
      getItemValue(filtersApplied, key),
    [filtersApplied],
  );

  const setFilterAppliedByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, value: any) => {
      setFilterByKey(key, value);

      const keyTyped = key as keyof OrderBillingPendingListFilters;

      setFilterApplied((prevFiltersApplied) => ({
        ...prevFiltersApplied,
        [keyTyped]: 'filters',
      }));
    },
    [setFilterByKey],
  );

  const handleChangeInputFilterApplied = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      setFilterAppliedByKey(name, value);
    },
    [setFilterAppliedByKey],
  );

  const clearFiltersApplied = useCallback(() => {
    clearFilters();

    setFilterApplied({
      company: 'filters',
      integrationStores: 'filters',
      giverCompanies: 'filters',
      dateFrom: 'filters',
      dateTo: 'filters',
      storeName: 'filters',
      customers: 'filters',
    });
  }, [clearFilters, setFilterApplied]);

  const getValueQueryParams = useCallback(
    <Key extends keyof OrderBillingPendingListQueryParamsFilters>(
      key: Key,
    ): OrderBillingPendingListQueryParamsFilters[Key] =>
      getItemValue(queryFilters, key),
    [queryFilters],
  );

  const getValueInitialFilters = useCallback(
    <Key extends keyof OrderBillingPendingListFilters>(
      key: Key,
    ): OrderBillingPendingListFilters[Key] => getItemValue(initialFilters, key),
    [initialFilters],
  );

  const getValueCurrentFilters = useCallback(
    <Key extends keyof OrderBillingPendingListFilters>(
      key: Key,
    ): OrderBillingPendingListFilters[Key] => getItemValue(filters, key),
    [filters],
  );

  const getFilteredArrayValueByQueryParam = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      key,
      currentSelectData,
      getExtractorKey,
    }: {
      key: 'company' | 'integrationStores' | 'giverCompanies';
      currentSelectData: OrderBillingPendingListParamsSelectData<T>;
      getExtractorKey: (data: T) => string;
    }) => {
      const currentArrayQueryParam = getValueQueryParams(key);

      if (
        currentArrayQueryParam !== undefined &&
        currentArrayQueryParam !== null &&
        !currentSelectData.loading
      ) {
        const filteredValues = filter(
          currentSelectData.list,
          (currentOption) => {
            const currentOptionId = getExtractorKey(currentOption);

            if (isArray(currentArrayQueryParam)) {
              return includes(currentArrayQueryParam, currentOptionId);
            }

            return isEqual(currentArrayQueryParam, currentOptionId);
          },
        );

        return filteredValues;
      }

      return null;
    },
    [getValueQueryParams],
  );

  const setCompaniesByQueryParam = useCallback(() => {
    let options: BillingItemCompanyBase[] = [];

    const companiesData = getFilteredArrayValueByQueryParam({
      key: 'company',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (companiesData && companiesData.length > 0) {
      const currentCompany = companiesData[0];

      options = options.concat(currentCompany);
      setFilterByKey('company', currentCompany);
    }

    const integrationStoresData = getFilteredArrayValueByQueryParam({
      key: 'integrationStores',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (integrationStoresData && integrationStoresData.length > 0) {
      options = options.concat(integrationStoresData);
      setFilterByKey('integrationStores', integrationStoresData);
    }

    const giverCompaniesData = getFilteredArrayValueByQueryParam({
      key: 'giverCompanies',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (giverCompaniesData && giverCompaniesData.length > 0) {
      options = options.concat(giverCompaniesData);
      setFilterByKey('giverCompanies', giverCompaniesData);
    }

    if (options.length > 0) {
      selectsData.companies.autocompleteRef.current?.setValue(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getFilteredArrayValueByQueryParam,
    selectsData.companies.list,
    selectsData.companies.loading,
    setFilterByKey,
  ]);

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setCompaniesByQueryParam();
    }
  }, [hasAccesMgmBillingGiver, setCompaniesByQueryParam]);

  const setDateByQueryParam = useCallback(
    (key: 'dateFrom' | 'dateTo') => {
      const currentDateQueryParam = getValueQueryParams(key);
      const currentDateDefault = getValueInitialFilters(key);

      if (currentDateQueryParam) {
        const valDate = new Date(currentDateQueryParam);

        const parseValDate =
          valDate.toString() !== 'Invalid Date' ? valDate : currentDateDefault;

        setFilterByKey(key, parseValDate);
      }
    },
    [getValueQueryParams, getValueInitialFilters, setFilterByKey],
  );

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setDateByQueryParam('dateFrom');
      setDateByQueryParam('dateTo');
    }
  }, [hasAccesMgmBillingGiver, setDateByQueryParam]);

  const setStoreNameByQueryParam = useCallback(() => {
    const currentStoreNameQueryParam = getValueQueryParams('storeName');
    const currentStoreNameDefault = getValueInitialFilters('storeName');

    const parseStoreName =
      currentStoreNameQueryParam || currentStoreNameDefault;

    setFilterByKey('storeName', parseStoreName);
  }, [getValueQueryParams, getValueInitialFilters, setFilterByKey]);

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setStoreNameByQueryParam();
    }
  }, [hasAccesMgmBillingGiver, setStoreNameByQueryParam]);

  const parseCompanyRequest = useCallback(() => {
    const currentTypeCompanyFilterAppliedCompany =
      getFilterAppliedByKey('company');

    const currentCompanyQueryParam = getValueQueryParams('company');
    const currentCompanyFilter = getValueCurrentFilters('company');

    let id: Nullable<string> = null;

    if (
      currentTypeCompanyFilterAppliedCompany === 'queryParams' &&
      currentCompanyQueryParam
    ) {
      id = currentCompanyQueryParam;
    }

    if (
      currentTypeCompanyFilterAppliedCompany === 'filters' &&
      currentCompanyFilter
    ) {
      id = getCompanyToBillingItemId(currentCompanyFilter);
    }

    return id;
  }, [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters]);

  const parseOthersCompaniesRequest = useCallback(
    (key: 'integrationStores' | 'giverCompanies') => {
      const currentTypeOthersCompaniesFilterAppliedCompanies =
        getFilterAppliedByKey(key);

      let ids: Nullable<string[]> = null;

      const currentOthersCompaniesQueryParam = getValueQueryParams(key);
      const currentOthersCompaniesFilter = getValueCurrentFilters(key);

      if (
        currentTypeOthersCompaniesFilterAppliedCompanies === 'queryParams' &&
        currentOthersCompaniesQueryParam
      ) {
        ids = isArray(currentOthersCompaniesQueryParam)
          ? currentOthersCompaniesQueryParam
          : [currentOthersCompaniesQueryParam];
      }

      if (
        currentTypeOthersCompaniesFilterAppliedCompanies === 'filters' &&
        currentOthersCompaniesFilter &&
        currentOthersCompaniesFilter.length > 0
      ) {
        ids = currentOthersCompaniesFilter.map((company) =>
          getCompanyToBillingItemId(company),
        );
      }

      return ids;
    },
    [getFilterAppliedByKey, getValueCurrentFilters, getValueQueryParams],
  );

  const parseDateRequest = useCallback(
    (key: 'dateFrom' | 'dateTo') => {
      const currentTypeDateFilterAppliedCurrentDate =
        getFilterAppliedByKey(key);

      const currentDateQueryParam = getValueQueryParams(key);
      const currentDateFilter = getValueCurrentFilters(key);

      const currentDate =
        currentTypeDateFilterAppliedCurrentDate === 'queryParams'
          ? currentDateQueryParam
          : currentDateFilter;

      if (!currentDate) return null;

      const formatDate = formatterDate(currentDate, {
        format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
      });

      return formatDate || null;
    },
    [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters],
  );

  const parseStoreNameRequest = useCallback(() => {
    const currentTypeDateFilterAppliedStoreName =
      getFilterAppliedByKey('storeName');

    const currentStoreNameQueryParam = getValueQueryParams('storeName');
    const currentStoreNameFilter = getValueCurrentFilters('storeName');

    const currentStoreName =
      currentTypeDateFilterAppliedStoreName === 'queryParams'
        ? currentStoreNameQueryParam
        : currentStoreNameFilter;

    return currentStoreName || null;
  }, [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters]);

  const parseCustomersRequest = useCallback(() => {
    const currentCustomersFilter = getValueCurrentFilters('customers');

    let ids: Nullable<string[]> = null;

    if (currentCustomersFilter && currentCustomersFilter.length > 0) {
      ids = currentCustomersFilter.map((customer) => customer.customerId);
    }

    return ids;
  }, [getValueCurrentFilters]);

  const parseFiltersRequest = useMemo(
    (): BillingItemsFilter => ({
      companyIdParam: parseCompanyRequest(),
      companyIntegrationStoreIds:
        parseOthersCompaniesRequest('integrationStores'),
      giverCompanyIds: parseOthersCompaniesRequest('giverCompanies'),
      dateTimeFrom: parseDateRequest('dateFrom'),
      dateTimeTo: parseDateRequest('dateTo'),
      storeName: parseStoreNameRequest(),
      customerIds: parseCustomersRequest(),
    }),
    [
      parseCompanyRequest,
      parseOthersCompaniesRequest,
      parseDateRequest,
      parseStoreNameRequest,
      parseCustomersRequest,
    ],
  );

  return {
    filters,
    setFilterByKey: setFilterAppliedByKey,
    handleChangeInputFilter: handleChangeInputFilterApplied,
    clearFilters: clearFiltersApplied,
    parseFiltersRequest,
  };
};
